import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/design06/Header';
import {
    Coffee,
    Award,
    Heart,
    Shield,
    Star,
    ArrowLeft,
    CheckCircle,
    Thermometer,
    Zap,
    Globe,
    ArrowRight,
    Target,
} from 'lucide-react';

const CinnamonPage = () => {
    useEffect(() => {
        const setNamedMeta = (name, content) => {
            let tag = document.querySelector(`meta[name="${name}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('name', name);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        const setPropertyMeta = (property, content) => {
            let tag = document.querySelector(`meta[property="${property}"]`);
            if (!tag) {
                tag = document.createElement('meta');
                tag.setAttribute('property', property);
                document.head.appendChild(tag);
            }
            tag.setAttribute('content', content);
        };

        const setCanonical = (href) => {
            let link = document.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement('link');
                link.setAttribute('rel', 'canonical');
                document.head.appendChild(link);
            }
            link.setAttribute('href', href);
        };

        document.title = "Ceylon Cinnamon - The True Cinnamon | Claude's Special";
        setNamedMeta(
            'description',
            "Discover authentic Ceylon Cinnamon from Sri Lanka. Learn about the world's finest cinnamon variety, its health benefits, and why it's considered the gold standard of spices."
        );
        setNamedMeta(
            'keywords',
            'Ceylon Cinnamon, true cinnamon, Cinnamomum verum, Sri Lankan cinnamon, health benefits, premium spices'
        );
        setPropertyMeta('og:title', 'Ceylon Cinnamon - The True Cinnamon');
        setPropertyMeta(
            'og:description',
            "Authentic Ceylon Cinnamon from Sri Lanka - the world's finest cinnamon with unmatched quality and health benefits."
        );
        setPropertyMeta('og:type', 'article');
        setCanonical('/ceylon-cinnamon');
    }, []);

    return (
        <>
            <Header />

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                        alt="Ceylon Cinnamon"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
                </div>

                <div className="relative z-10 text-center max-w-6xl mx-auto px-8 pt-20">
                    <div className="mb-12">
                        <Link
                            to="/"
                            className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-6 py-3 mb-8 hover:bg-white/20 transition-colors duration-200 font-medium rounded-full"
                        >
                            <ArrowLeft className="h-5 w-5" />
                            <span>Back to Home</span>
                        </Link>
                    </div>

                    <div className="mb-8">
                        <span className="inline-block px-6 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-200 text-sm font-medium tracking-wider uppercase rounded-full">
                            Cinnamomum Verum
                        </span>
                    </div>

                    <h1 className="text-6xl md:text-8xl font-serif font-bold mb-8 leading-tight">
                        <span className="block text-white mb-4">Ceylon</span>
                        <span className="block bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                            Cinnamon
                        </span>
                    </h1>

                    <p className="text-xl md:text-2xl mb-12 text-gray-200 max-w-4xl mx-auto leading-relaxed font-light">
                        The world&apos;s only true cinnamon, scientifically known as Cinnamomum verum,
                        cultivated exclusively in Sri Lanka&apos;s mystical highlands for over 2,000 years.
                    </p>
                </div>
            </section>

            {/* What Makes It Special */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="text-center mb-16">
                        <div className="mb-6">
                            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 text-sm font-medium tracking-wider uppercase rounded-full">
                                What Makes It Special
                            </span>
                        </div>
                        <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">
                            The Crown Jewel of Spices
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Ceylon Cinnamon stands apart from all other varieties with its unique characteristics,
                            unmatched quality, and extraordinary health benefits.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="group text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="relative mb-8">
                                    <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                                        <Coffee className="h-10 w-10 text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
                                        <Star className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">True Cinnamon</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    The only authentic cinnamon species recognized by botanists worldwide,
                                    native exclusively to Sri Lanka.
                                </p>
                            </div>
                        </div>

                        <div className="group text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="relative mb-8">
                                    <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                                        <Heart className="h-10 w-10 text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-green-400 to-emerald-400 rounded-full flex items-center justify-center">
                                        <CheckCircle className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Ultra-Safe</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Contains less than 0.004% coumarin compared to cassia&apos;s 5%, making it completely safe
                                    for daily consumption.
                                </p>
                            </div>
                        </div>

                        <div className="group text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="relative mb-8">
                                    <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                                        <Zap className="h-10 w-10 text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                                        <Star className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Exquisite Flavor</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Delicate, sweet, and complex flavor profile with subtle citrus notes that enhance
                                    culinary creations.
                                </p>
                            </div>
                        </div>

                        <div className="group text-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="relative mb-8">
                                    <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-all duration-300">
                                        <Award className="h-10 w-10 text-white" />
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-blue-400 to-indigo-400 rounded-full flex items-center justify-center">
                                        <Globe className="h-4 w-4 text-white" />
                                    </div>
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Global Recognition</h3>
                                <p className="text-gray-600 leading-relaxed">
                                    Preferred by world-renowned chefs, health experts, and discerning consumers
                                    worldwide.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Health Benefits */}
            <section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="text-center mb-16">
                        <div className="mb-6">
                            <span className="inline-block px-4 py-2 bg-green-100 text-green-800 text-sm font-medium tracking-wider uppercase rounded-full">
                                Health &amp; Wellness
                            </span>
                        </div>
                        <h2 className="text-5xl font-serif font-bold text-gray-900 mb-6">
                            Nature&apos;s Most Powerful Wellness Spice
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Ceylon Cinnamon offers remarkable health benefits backed by scientific research,
                            making it nature&apos;s perfect wellness companion.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Heart className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cardiovascular Support</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Supports healthy cholesterol levels and promotes optimal blood circulation throughout the body.
                                </p>
                                <div className="flex items-center text-red-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Clinically Studied</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Thermometer className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Blood Sugar Balance</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    May help support healthy blood sugar levels and improve insulin sensitivity naturally.
                                </p>
                                <div className="flex items-center text-blue-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Research Backed</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Shield className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Antioxidant Powerhouse</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Packed with powerful antioxidants that help protect cells from oxidative stress and aging.
                                </p>
                                <div className="flex items-center text-green-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>High ORAC Value</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Zap className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Anti-Inflammatory</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Natural anti-inflammatory compounds help reduce inflammation markers throughout the body.
                                </p>
                                <div className="flex items-center text-yellow-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Natural Compounds</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <Target className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Cognitive Support</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    May support brain health and cognitive function through neuroprotective compounds.
                                </p>
                                <div className="flex items-center text-purple-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>Neuroprotective</span>
                                </div>
                            </div>
                        </div>

                        <div className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden">
                            <div className="p-8">
                                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                                    <CheckCircle className="h-8 w-8 text-white" />
                                </div>
                                <h3 className="text-2xl font-semibold text-gray-900 mb-4">Safe Daily Use</h3>
                                <p className="text-gray-600 leading-relaxed mb-6">
                                    Ultra-low coumarin content makes it completely safe for daily consumption without concerns.
                                </p>
                                <div className="flex items-center text-teal-600 font-medium">
                                    <CheckCircle className="h-5 w-5 mr-2" />
                                    <span>FDA Recognized</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 relative overflow-hidden">
                <div className="absolute inset-0">
                    <img
                        src="https://images.pexels.com/photos/4198006/pexels-photo-4198006.jpeg"
                        alt="Ceylon Cinnamon"
                        className="w-full h-full object-cover opacity-20"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80"></div>
                </div>

                <div className="max-w-5xl mx-auto text-center px-8 lg:px-16 relative">
                    <div className="mb-8">
                        <span className="inline-block px-6 py-2 bg-amber-600/20 backdrop-blur-sm border border-amber-400/30 text-amber-200 text-sm font-medium tracking-wider uppercase rounded-full">
                            Ready to Experience?
                        </span>
                    </div>

                    <h2 className="text-5xl md:text-6xl font-serif font-bold text-white mb-8 leading-tight">
                        Experience the True Difference of
                        <br />
                        <span className="bg-gradient-to-r from-amber-300 via-orange-400 to-amber-500 bg-clip-text text-transparent">
                            Authentic Ceylon Cinnamon
                        </span>
                    </h2>

                    <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto">
                        Join Australia&apos;s leading importers and health-conscious consumers who choose authentic Ceylon Cinnamon
                        for its unmatched quality, safety, and extraordinary health benefits.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
                        <Link
                            to="/#contact"
                            className="group bg-gradient-to-r from-amber-600 to-orange-700 hover:from-amber-700 hover:to-orange-800 text-white px-10 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl flex items-center space-x-3"
                        >
                            <span>Get Wholesale Pricing</span>
                            <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>
                        <a
                            href="mailto:exports@claudesspecial.lk"
                            className="bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white px-10 py-4 text-lg font-medium rounded-full transition-all duration-300 transform hover:scale-105"
                        >
                            Email Direct
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="max-w-7xl mx-auto px-8 lg:px-16">
                    <div className="text-center">
                        <div className="flex items-center justify-center space-x-4 mb-8">
                            <div className="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center">
                                <Coffee className="h-8 w-8 text-white" />
                            </div>
                            <div>
                                <div className="text-3xl font-serif font-bold text-white">Claude&apos;s Special</div>
                                <div className="text-amber-400 font-medium">Authentic Ceylon Cinnamon</div>
                            </div>
                        </div>
                        <div className="w-24 h-0.5 bg-gradient-to-r from-amber-500 to-orange-600 mx-auto mb-8"></div>
                        <p className="text-gray-300 mb-8 max-w-3xl mx-auto text-lg">
                            The world&apos;s finest Ceylon Cinnamon, sourced directly from Sri Lanka&apos;s pristine highlands
                            and delivered to Australia with uncompromising quality and authenticity.
                        </p>
                        <div className="flex justify-center space-x-12 text-gray-300 mb-8">
                            <span className="flex items-center space-x-2">
                                <Coffee className="h-5 w-5 text-amber-400" />
                                <span className="font-medium">Premium Quality</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Shield className="h-5 w-5 text-green-400" />
                                <span className="font-medium">Safe Daily Use</span>
                            </span>
                            <span className="flex items-center space-x-2">
                                <Globe className="h-5 w-5 text-blue-400" />
                                <span className="font-medium">Global Export</span>
                            </span>
                        </div>
                        <p className="text-gray-500 font-medium">
                            &copy; 2025 Claude&apos;s Special. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </>
    );
};

export default CinnamonPage;
